// src/components/home/MyLinksCard.js
//
// "My quick links" on the home page (UI revamp): the person's own bookmarks,
// up to 12, as tiles (letter + label + host name). https only, no sign-in
// details in an address (checked here and again by saveHomePrivate). Nothing is
// fetched from the linked sites (no favicons). Private; saved by useHomePrivate.
//
// Pinned RegX pages (2026-11-02): on top, the person can pin up to 12 pages
// from their own sidebar (`pinnable` = that role's sidebar items, so only pages
// they can already open are offered or shown). A pin is the item's id; opening
// it goes through the sidebar's own handler (`onOpenPage`).

import { useMemo, useState } from "react";
import { Pin, Plus, X } from "lucide-react";
import { LIMITS, addLink, removeLink, hostnameOf, pinPage, unpinPage } from "../../services/homePrivate";
import { navIcon } from "./navIcons";
import SaveStatus from "./SaveStatus";

export default function MyLinksCard({ store, pinnable = [], onOpenPage = () => {} }) {
  const { data, loaded, loadError, status, errorText, update } = store;
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);
  const [picking, setPicking] = useState(false);
  const [pinError, setPinError] = useState("");
  const full = data.links.length >= LIMITS.links;
  const pinIds = useMemo(() => data.pins || [], [data.pins]);
  // Only pins this role can still open (a role change hides, never deletes, a pin).
  const pinned = useMemo(
    () => pinIds.map((id) => pinnable.find((s) => s.id === id)).filter(Boolean),
    [pinIds, pinnable]
  );
  const pinsFull = pinIds.length >= LIMITS.pins;

  const pin = (s) => {
    const r = pinPage(pinIds, s.id);
    if (!r.ok) {
      setPinError(r.error);
      return;
    }
    setPinError("");
    setPicking(false);
    update((d) => ({ ...d, pins: r.pins }));
  };

  const add = (e) => {
    e.preventDefault();
    const r = addLink(data.links, { label, url });
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setError("");
    setLabel("");
    setUrl("");
    setAdding(false);
    update((d) => ({ ...d, links: r.links }));
  };

  return (
    <section className="rx-home-card" aria-label="My quick links">
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h2 className="rx-home-title">My quick links</h2>
        <SaveStatus status={status} errorText={errorText} />
      </div>
      {loadError ? (
        <p className="rx-home-muted text-sm">{loadError}</p>
      ) : !loaded ? (
        <p className="rx-home-muted text-sm">Loading…</p>
      ) : (
        <>
          {pinnable.length > 0 && (
            <div role="group" aria-label="Pinned RegX pages" className="mb-4 pb-4 rx-home-split">
              <p className="rx-home-muted text-xs mb-2">RegX pages</p>
              {pinned.length === 0 ? (
                <p className="rx-home-muted text-sm">Pin the RegX pages you visit most.</p>
              ) : (
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {pinned.map((s) => {
                    const Icon = navIcon(s.icon);
                    return (
                      <li key={s.id} className="rx-link-tile">
                        <button type="button" onClick={() => onOpenPage(s)} className="flex items-center gap-2.5 min-w-0 flex-1 text-left">
                          <span className="rx-pin-icon" aria-hidden="true">
                            <Icon className="w-3.5 h-3.5" />
                          </span>
                          <span className="text-sm font-medium truncate">{s.label}</span>
                        </button>
                        <button
                          type="button"
                          className="rx-icon-button shrink-0"
                          aria-label={`Unpin ${s.label}`}
                          title="Unpin"
                          onClick={() => update((d) => ({ ...d, pins: unpinPage(d.pins, s.id) }))}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {picking ? (
                <div className="mt-3">
                  <p className="rx-home-muted text-xs mb-1">Pick a page from your menu:</p>
                  <ul aria-label="Pages you can pin" className="max-h-64 overflow-y-auto">
                    {pinnable.map((s) => {
                      const Icon = navIcon(s.icon);
                      const isPinned = pinIds.includes(s.id);
                      return (
                        <li key={s.id}>
                          <button type="button" className="rx-pin-option" disabled={isPinned} onClick={() => pin(s)}>
                            <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                            <span className="text-sm flex-1 truncate">{s.label}</span>
                            {isPinned && <span className="text-[11px]">Pinned</span>}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  <button type="button" className="rx-home-link mt-2" onClick={() => setPicking(false)}>
                    Done
                  </button>
                </div>
              ) : (
                !pinsFull && (
                  <button type="button" onClick={() => setPicking(true)} className="rx-home-link inline-flex items-center gap-1 mt-3">
                    <Pin className="w-3.5 h-3.5" />
                    Pin a RegX page
                  </button>
                )
              )}
              {pinError && <p className="rx-home-warn text-xs mt-2">{pinError}</p>}
              {pinsFull && !picking && <p className="rx-home-muted text-xs mt-3">You have {LIMITS.pins} pages pinned, the most you can keep.</p>}
            </div>
          )}
          {pinnable.length > 0 && <p className="rx-home-muted text-xs mb-2">Websites</p>}
          {data.links.length === 0 ? (
            <p className="rx-home-muted text-sm">No links yet. Add the sites you open every day.</p>
          ) : (
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {data.links.map((l) => (
                <li key={l.id} className="rx-link-tile">
                  <a href={l.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 min-w-0 flex-1">
                    <span className="rx-home-initials" aria-hidden="true">
                      {(l.label || hostnameOf(l.url) || "?").charAt(0).toUpperCase()}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium truncate">{l.label}</span>
                      <span className="block rx-home-muted text-[11px] truncate">{hostnameOf(l.url)}</span>
                    </span>
                  </a>
                  <button
                    type="button"
                    className="rx-icon-button shrink-0"
                    aria-label={`Remove ${l.label}`}
                    title="Remove"
                    onClick={() => update((d) => ({ ...d, links: removeLink(d.links, l.id) }))}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {adding ? (
            <form onSubmit={add} className="mt-3 space-y-2">
              <div>
                <label htmlFor="rx-link-url" className="rx-home-muted text-xs block mb-1">
                  Address
                </label>
                <input
                  id="rx-link-url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  maxLength={LIMITS.linkUrl}
                  placeholder="example.com"
                  className="rx-home-input w-full"
                  autoComplete="off"
                />
              </div>
              <div>
                <label htmlFor="rx-link-label" className="rx-home-muted text-xs block mb-1">
                  Name (optional)
                </label>
                <input
                  id="rx-link-label"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  maxLength={LIMITS.linkLabel}
                  placeholder="Defaults to the site's name"
                  className="rx-home-input w-full"
                />
              </div>
              {error && <p className="rx-home-warn text-xs">{error}</p>}
              <div className="flex items-center gap-2">
                <button type="submit" className="rx-home-button">
                  Add link
                </button>
                <button
                  type="button"
                  className="rx-home-link"
                  onClick={() => {
                    setAdding(false);
                    setError("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            !full && (
              <button type="button" onClick={() => setAdding(true)} className="rx-home-link inline-flex items-center gap-1 mt-3">
                <Plus className="w-3.5 h-3.5" />
                Add a link
              </button>
            )
          )}
          {full && !adding && <p className="rx-home-muted text-xs mt-3">You have {LIMITS.links} links, the most you can keep.</p>}
        </>
      )}
      <p className="rx-home-muted text-[11px] mt-3">
        Private to you. Don't link to patient records or put sign-in details in an address.
      </p>
    </section>
  );
}
